// Data service for progression persistence using Supabase
import { supabase } from '../config/supabase';
import type { Progression } from '../presets/presetSchema';
import type { UserTier } from '../auth/types';

/**
 * Save user progression to Supabase
 */
export const saveUserProgression = async (userId: string, progression: Progression) => {
  try {
    const { error } = await supabase
      .from('presets')
      .upsert({
        id: progression.id,
        user_id: userId,
        name: progression.name,
        data: progression
      });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Get user progressions from Supabase
 */
export const getUserProgressionsFromDB = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('presets')
      .select('data')
      .eq('user_id', userId);

    if (error) {
      return { success: false, error: error.message };
    }

    const progressions = data?.map(row => row.data as Progression) || [];
    return { success: true, data: progressions };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Delete user progression from Supabase
 */
export const deleteUserProgression = async (userId: string, progressionId: string) => {
  try {
    const { error } = await supabase
      .from('presets')
      .delete()
      .eq('user_id', userId)
      .eq('id', progressionId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Get user profile from Supabase
 */
export const getUserProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('tier')
      .eq('id', userId)
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Update user tier in profile
 */
export const updateUserTier = async (userId: string, tier: UserTier) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ tier })
      .eq('id', userId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
